import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'; // Effect on Data
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PersonPinCircleOutlinedIcon from '@mui/icons-material/PersonPinCircleOutlined'; // Researcher Position
import TrackChangesIcon from '@mui/icons-material/TrackChanges'; // Signal Moment
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const ReflexivityTrackerAnalysisTranscript = ({ summaryData }) => {
  const [expandedSignal, setExpandedSignal] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSignal(isExpanded ? panel : false);
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Reflexivity Tracker analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;

  // Helper to truncate long text for summary
  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Reflexivity Tracker Analysis
          <AnalysisLensHelperText lensName="reflexivity_tracker" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Overall Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {results.summary || 'No summary provided.'}
          </Typography>
        </Box>

        {/* Signals Accordions */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Identified Reflexivity Signals
        </Typography>

        {results.signals?.length > 0 ? (
          results.signals.map((signal, index) => {
            const panelId = `signal-panel-${index}`;
            const isExpanded = expandedSignal === panelId;

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1.5,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: '0 0 12px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    bgcolor: isExpanded ? 'grey.50' : 'background.paper',
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: 'divider',
                    borderTopLeftRadius: 'inherit',
                    borderTopRightRadius: 'inherit',
                    minHeight: 64,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <TrackChangesIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                      {/* Show truncated moment in summary */}
                      {truncateText(signal.moment)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={2.5}>
                    {/* Moment (Full) */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <TrackChangesIcon fontSize="inherit" color="primary" /> Moment
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 3 }}>
                        {signal.moment || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Researcher Position */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <PersonPinCircleOutlinedIcon fontSize="inherit" color="secondary" />{' '}
                        Researcher Position
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 3 }}>
                        {signal.researcher_position || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Effect on Data */}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <EditNoteOutlinedIcon fontSize="inherit" color="info" /> Effect on Data
                      </Typography>
                      <Typography variant="body2" sx={{ pl: 3 }}>
                        {signal.effect_on_data || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Quotes */}
                    {signal.quotes?.length > 0 && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <FormatQuoteIcon fontSize="inherit" color="action" /> Illustrative Quotes
                        </Typography>
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {signal.quotes.map((quote, idx) => (
                            <ListItem
                              key={idx}
                              disableGutters
                              sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 1 } }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                <FormatQuoteIcon
                                  fontSize="small"
                                  sx={{ transform: 'scaleX(-1)' }}
                                  color="disabled"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No specific reflexivity signals identified in this analysis.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ReflexivityTrackerAnalysisTranscript;
